import {Button, Col, Container, FloatingLabel, Form, Modal, Row} from "react-bootstrap";
import React, {Fragment, useContext, useEffect, useRef, useState} from "react";
import {AgGridReact} from "ag-grid-react";
import {UsacmContext} from "../App";
import {deleteCourseCoupon, getCourseCouponList, getCourseList, upsertCourseCoupon} from "../api/CourseApi";
import {toolTipRenderer} from "../utils/gridUtils";
import {centsToDollarStr, centsToDollarStrWSign, dollarsToCents, getConfLabel, showSuccessToast, titleCase}
  from "../utils/usacmUtils";
import {fieldHasErrors, getErrorMessageForField} from "../utils/formUtils";
import {UserSelect} from "../shared/UserSelect";
import {CONF_LABEL_KEY_COURSE} from "../constants";

/**
 * Course Settings -> Coupons (course admins only).  The codes a buyer can type in their cart for a discount - a
 * percent or an amount off every course in the cart, or off one course.  A cart holds one coupon at a time.
 * Amounts are stored in cents and edited in dollars.  A coupon which is on an order can't be deleted - the server
 * refuses, and it should be made inactive instead.
 */
export function CourseSettingsCoupons() {
  const context = useContext(UsacmContext);
  const [conf,] = context.conference;
  const [couponList, setCouponList] = useState([]);
  const [courseList, setCourseList] = useState([]);
  const [errors, setErrors] = useState([]);
  // The coupon in the add/edit dialog - {} while adding a new one, null when the dialog is closed
  const [editingCoupon, setEditingCoupon] = useState(null);
  const [deletingCoupon, setDeletingCoupon] = useState(null);
  // Add/edit dialog fields
  const [editCode, setEditCode] = useState('');
  const [editDescription, setEditDescription] = useState('');
  const [editCourseId, setEditCourseId] = useState('');
  const [editUsingPercent, setEditUsingPercent] = useState(true);
  const [editPercent, setEditPercent] = useState('');
  const [editAmount, setEditAmount] = useState('');
  const [editTotalCount, setEditTotalCount] = useState('');
  const [editUnlimitedCount, setEditUnlimitedCount] = useState(false);
  const [editAllowedUsers, setEditAllowedUsers] = useState([]);
  const [editStartDate, setEditStartDate] = useState('');
  const [editEndDate, setEditEndDate] = useState('');
  const [editActive, setEditActive] = useState(true);
  const [editErrors, setEditErrors] = useState([]);
  const gridRef = useRef();
  const hasFewRows = (couponList || []).length < 8;
  const courseLabel = titleCase(getConfLabel(conf, CONF_LABEL_KEY_COURSE));
  const isAdding = !!editingCoupon && !editingCoupon.id;

  useEffect(() => {
    getCourseCouponList((code, data, newErrors) => {
      if (code === 200) {
        setCouponList(data);
        setErrors([]);
      } else {
        setErrors(newErrors);
      }
    });
    getCourseList((code, data) => {
      if (code === 200) {
        setCourseList(data);
      }
    });
  }, []);

  function startEditingCoupon(coupon) {
    setEditCode(coupon.code || '');
    setEditDescription(coupon.description || '');
    setEditCourseId(coupon.course_id || '');
    setEditUsingPercent(!coupon.discount_amount);
    setEditPercent(coupon.discount_percent || '');
    setEditAmount(coupon.discount_amount ? centsToDollarStr(coupon.discount_amount) : '');
    setEditTotalCount(coupon.total_count || '');
    setEditUnlimitedCount(!!coupon.unlimited_count);
    setEditAllowedUsers(coupon.allowed_users || []);
    setEditStartDate(coupon.start_date || '');
    setEditEndDate(coupon.end_date || '');
    setEditActive(coupon.id ? !!coupon.active : true);
    setEditErrors([]);
    setEditingCoupon(coupon);
  }

  function callUpsertCoupon() {
    let discountPercent = null;
    let discountAmount = null;
    if (editUsingPercent) {
      discountPercent = editPercent === '' ? null : editPercent;
    } else {
      discountAmount = dollarsToCents(editAmount);
      if (discountAmount === null) {
        setEditErrors([{message: 'The amount must be a number, eg. 10.00', fields: ['discount_amount']}]);
        return;
      }
    }
    upsertCourseCoupon(editingCoupon?.id, editCode, editDescription, editCourseId || null, discountPercent,
      discountAmount, editUnlimitedCount || editTotalCount === '' ? null : editTotalCount, editUnlimitedCount,
      editAllowedUsers, editStartDate, editEndDate, editActive,
      (code, data, newErrors) => {
        if (code === 200) {
          showSuccessToast(isAdding ? 'Added coupon.' : 'Updated coupon.');
          setCouponList(data);
          setEditingCoupon(null);
          setEditErrors([]);
        } else {
          setEditErrors(newErrors);
        }
      });
  }

  function callDeleteCoupon() {
    deleteCourseCoupon(deletingCoupon?.id, (code, data, newErrors) => {
      setDeletingCoupon(null);
      if (code === 200) {
        showSuccessToast('Deleted coupon.');
        setCouponList(data);
        setErrors([]);
      } else {
        setErrors(newErrors);
      }
    });
  }

  function addAllowedUser(user) {
    if (user && !editAllowedUsers.find(u => u.id === user.id)) {
      setEditAllowedUsers([...editAllowedUsers, user]);
    }
  }

  function removeAllowedUser(user) {
    setEditAllowedUsers(editAllowedUsers.filter(u => u.id !== user.id));
  }

  function getDiscountStr(coupon) {
    if (coupon?.discount_percent) {
      return `${coupon.discount_percent}%`;
    }
    return centsToDollarStrWSign(coupon?.discount_amount);
  }

  // Column setup
  const colDefs = [
    {field: 'code', cellRenderer: toolTipRenderer, headerName: 'Code', flex: 2, minWidth: 130},
    {
      headerName: 'Discount', flex: 1, minWidth: 100,
      valueGetter: params => getDiscountStr(params.data),
    },
    {
      field: 'course_title', headerName: courseLabel, flex: 2, minWidth: 150, cellRenderer: toolTipRenderer,
      valueFormatter: params => params.value || 'Any',
    },
    {
      headerName: 'Used', flex: 1, minWidth: 90,
      headerTooltip: 'Orders it was used on / how many times it can be used',
      valueGetter: params => `${params.data.used_count} / ${params.data.unlimited_count ? 'Unlimited' : params.data.total_count}`,
    },
    {
      headerName: 'Users', flex: 1, minWidth: 90,
      headerTooltip: 'Who can use it - anyone, or only the users listed',
      valueGetter: params => params.data.allowed_users?.length ? params.data.allowed_users.length : 'Anyone',
    },
    {field: 'start_date', headerName: 'Starts', flex: 1, minWidth: 110},
    {field: 'end_date', headerName: 'Ends', flex: 1, minWidth: 110},
    {
      field: 'active', headerName: 'Active', flex: 1, minWidth: 90,
      valueFormatter: params => params.value ? 'Yes' : 'No',
      cellClass: params => params.value ? '' : 'text-danger',
    },
    {field: 'description', cellRenderer: toolTipRenderer, headerName: 'Description', flex: 3, minWidth: 150},
    {
      headerName: 'Action',
      sortable: false, filter: false,
      maxWidth: 150, minWidth: 150,
      cellRenderer: params => {
        return <Fragment>
          <Button type="button" size="sm" className="me-2" onClick={() => startEditingCoupon(params.data)}>
            Edit
          </Button>
          <Button type="button" variant="danger" size="sm" onClick={() => setDeletingCoupon(params.data)}>
            Delete
          </Button>
        </Fragment>;
      }
    },
  ];

  const defaultColDefs = {
    sortable: true,
    filter: false,
    resizable: true,
  }

  return (
    <Container fluid className="usacm-container-wide">

      <Row>
        <Col>
          <div className="ag-theme-alpine" style={{width: '100%', ...(hasFewRows ? {} : {height: '700px'})}}>
            <AgGridReact
              ref={gridRef}
              rowData={couponList}
              columnDefs={colDefs}
              defaultColDef={defaultColDefs}
              gridOptions={{enableCellTextSelection: true, ensureDomOrder: true}} // enable cell selection
              domLayout={hasFewRows ? "autoHeight" : "normal"}
              getRowId={(params) => params.data.id} // prevent re-rendering the whole table when data changes
            />
          </div>
        </Col>
      </Row>

      {getErrorMessageForField(errors, '') &&
        <Row>
          <Col className="text-center mt-3 usacm-error-message">
            {getErrorMessageForField(errors, '')}
          </Col>
        </Row>
      }

      <Row>
        <Col className="usacm-button-row mt-3">
          <Button type="button" onClick={() => startEditingCoupon({})}>Add Coupon</Button>
        </Col>
      </Row>

      <Modal show={!!editingCoupon}
             onHide={() => setEditingCoupon(null)}
             size="lg">
        <Modal.Header closeButton>
          <Modal.Title>{isAdding ? 'Add Coupon' : 'Edit Coupon'}</Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-3">
          <Row className="mb-3">
            <Col className="col-6">
              <Form.Group controlId="edit_coupon_code">
                <FloatingLabel controlId="edit_coupon_code" label="Code">
                  <Form.Control type="text"
                                placeholder="Code"
                                name="code"
                                value={editCode}
                                onChange={e => setEditCode(e.target.value.toUpperCase())}
                                isInvalid={fieldHasErrors(editErrors, 'code')}/>
                  <Form.Control.Feedback type="invalid">
                    {getErrorMessageForField(editErrors, 'code')}
                  </Form.Control.Feedback>
                </FloatingLabel>
                <Form.Text muted>What the buyer types in their cart (case doesn't matter)</Form.Text>
              </Form.Group>
            </Col>
            <Col className="col-6">
              <Form.Group controlId="edit_coupon_course_id">
                <FloatingLabel controlId="edit_coupon_course_id" label={`Applies To`}>
                  <Form.Control
                    className="form-select"
                    as="select"
                    value={editCourseId}
                    onChange={e => setEditCourseId(e.target.value)}
                    isInvalid={fieldHasErrors(editErrors, 'course_id')}
                  >
                    <option value="">Any {courseLabel.toLowerCase()} in the cart</option>
                    {courseList.map(course =>
                      <option value={course.id} key={course.id}>{course.title}</option>)}
                  </Form.Control>
                  <Form.Control.Feedback type="invalid">
                    {getErrorMessageForField(editErrors, 'course_id')}
                  </Form.Control.Feedback>
                </FloatingLabel>
              </Form.Group>
            </Col>
          </Row>

          <Row className="mb-3">
            <Col>
              <Form.Group controlId="edit_coupon_description">
                <FloatingLabel controlId="edit_coupon_description" label="Description (optional)">
                  <Form.Control type="text"
                                placeholder="Description"
                                name="description"
                                value={editDescription}
                                onChange={e => setEditDescription(e.target.value)}
                                isInvalid={fieldHasErrors(editErrors, 'description')}/>
                  <Form.Control.Feedback type="invalid">
                    {getErrorMessageForField(editErrors, 'description')}
                  </Form.Control.Feedback>
                </FloatingLabel>
                <Form.Text muted>Shown with the code on the cart and the receipt, eg. "Early bird discount"</Form.Text>
              </Form.Group>
            </Col>
          </Row>

          <Row className="mb-3 align-items-center">
            <Col className="col-6">
              <Form.Check inline type="radio" id="edit_coupon_percent" name="edit_coupon_discount_type"
                          label="Percent off" checked={editUsingPercent}
                          onChange={() => setEditUsingPercent(true)}/>
              <Form.Check inline type="radio" id="edit_coupon_amount" name="edit_coupon_discount_type"
                          label="Amount off" checked={!editUsingPercent}
                          onChange={() => setEditUsingPercent(false)}/>
            </Col>
            <Col className="col-6">
              {editUsingPercent ?
                <Form.Group controlId="edit_coupon_discount_percent">
                  <FloatingLabel controlId="edit_coupon_discount_percent" label="Percent (%)">
                    <Form.Control type="number"
                                  step="1"
                                  min="1"
                                  max="100"
                                  placeholder="Percent"
                                  name="discount_percent"
                                  value={editPercent}
                                  onChange={e => setEditPercent(e.target.value)}
                                  isInvalid={fieldHasErrors(editErrors, 'discount_percent')}/>
                    <Form.Control.Feedback type="invalid">
                      {getErrorMessageForField(editErrors, 'discount_percent')}
                    </Form.Control.Feedback>
                  </FloatingLabel>
                </Form.Group>
                :
                <Form.Group controlId="edit_coupon_discount_amount">
                  <FloatingLabel controlId="edit_coupon_discount_amount" label="Amount ($)">
                    <Form.Control type="number"
                                  step="0.01"
                                  min="0"
                                  placeholder="Amount"
                                  name="discount_amount"
                                  value={editAmount}
                                  onChange={e => setEditAmount(e.target.value)}
                                  isInvalid={fieldHasErrors(editErrors, 'discount_amount')}/>
                    <Form.Control.Feedback type="invalid">
                      {getErrorMessageForField(editErrors, 'discount_amount')}
                    </Form.Control.Feedback>
                  </FloatingLabel>
                  <Form.Text muted>Never takes off more than the {courseLabel.toLowerCase()}s it applies to</Form.Text>
                </Form.Group>
              }
            </Col>
          </Row>

          <Row className="mb-3 align-items-center">
            <Col className="col-6">
              <Form.Group controlId="edit_coupon_total_count">
                <FloatingLabel controlId="edit_coupon_total_count" label="Times It Can Be Used">
                  <Form.Control type="number"
                                step="1"
                                min="1"
                                placeholder="Times It Can Be Used"
                                name="total_count"
                                disabled={editUnlimitedCount}
                                value={editUnlimitedCount ? '' : editTotalCount}
                                onChange={e => setEditTotalCount(e.target.value)}
                                isInvalid={fieldHasErrors(editErrors, 'total_count')}/>
                  <Form.Control.Feedback type="invalid">
                    {getErrorMessageForField(editErrors, 'total_count')}
                  </Form.Control.Feedback>
                </FloatingLabel>
                <Form.Text muted>Counts orders checked out with it, by anyone</Form.Text>
              </Form.Group>
            </Col>
            <Col className="col-6">
              <Form.Check
                type="checkbox"
                label="Unlimited"
                id="edit_coupon_unlimited_count"
                checked={editUnlimitedCount}
                onChange={e => setEditUnlimitedCount(e.target.checked)}
              />
            </Col>
          </Row>

          <Row className="mb-3">
            <Col className="col-6">
              <Form.Group controlId="edit_coupon_start_date">
                <FloatingLabel controlId="edit_coupon_start_date" label="Valid From (optional)">
                  <Form.Control type="date"
                                placeholder="Valid From"
                                name="start_date"
                                value={editStartDate}
                                onChange={e => setEditStartDate(e.target.value)}
                                isInvalid={fieldHasErrors(editErrors, 'start_date')}/>
                  <Form.Control.Feedback type="invalid">
                    {getErrorMessageForField(editErrors, 'start_date')}
                  </Form.Control.Feedback>
                </FloatingLabel>
              </Form.Group>
            </Col>
            <Col className="col-6">
              <Form.Group controlId="edit_coupon_end_date">
                <FloatingLabel controlId="edit_coupon_end_date" label="Valid Until (optional)">
                  <Form.Control type="date"
                                placeholder="Valid Until"
                                name="end_date"
                                value={editEndDate}
                                onChange={e => setEditEndDate(e.target.value)}
                                isInvalid={fieldHasErrors(editErrors, 'end_date')}/>
                  <Form.Control.Feedback type="invalid">
                    {getErrorMessageForField(editErrors, 'end_date')}
                  </Form.Control.Feedback>
                </FloatingLabel>
              </Form.Group>
            </Col>
          </Row>

          <Row className="mb-3">
            <Col>
              <div className="fw-semibold mb-2">Only These Users (leave empty for anyone)</div>
              {editAllowedUsers.map(user =>
                <div className="ms-2 mb-2 text-nowrap" key={user.id}>
                  {user.first_name} {user.last_name}, {user.email}
                  <Button type="button" variant="outline-danger" size="sm" className="ms-2"
                          onClick={() => removeAllowedUser(user)}>Remove</Button>
                </div>)}
              <UserSelect onChange={addAllowedUser} usersInConf={true} noneSelectedText="Add User..."/>
            </Col>
          </Row>

          <Row className="mb-2">
            <Col>
              <Form.Check
                type="checkbox"
                label="Active - can be used (uncheck to retire a coupon which is on orders)"
                id="edit_coupon_active"
                checked={editActive}
                onChange={e => setEditActive(e.target.checked)}
              />
            </Col>
          </Row>

          {getErrorMessageForField(editErrors, '') &&
            <Row>
              <Col className="text-center mt-3 usacm-error-message">
                {getErrorMessageForField(editErrors, '')}
              </Col>
            </Row>
          }
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setEditingCoupon(null)}>Cancel</Button>
          <Button variant="primary"
                  disabled={!editCode.trim()}
                  onClick={() => callUpsertCoupon()}>
            {isAdding ? 'Add Coupon' : 'Update Coupon'}
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal show={!!deletingCoupon}
             onHide={() => setDeletingCoupon(null)}
             size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Confirm Delete</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>
            Are you sure you want to delete the coupon {deletingCoupon?.code} ({getDiscountStr(deletingCoupon)} off)?
          </p>
          <p className="mb-0">
            A coupon which is already on an order (or in someone's cart) can't be deleted - uncheck Active on it instead.
          </p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setDeletingCoupon(null)}>Cancel</Button>
          <Button variant="danger" onClick={() => callDeleteCoupon()}>Delete Coupon</Button>
        </Modal.Footer>
      </Modal>

    </Container>
  );
}
